import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../ui/dropdown';
import { Avatar } from '../ui/avatar';
import { NavbarItem } from '../ui/navbar';
import { hasRole, useAuth } from './AuthProvider';
import { member } from '../lib/api';
import type { UserProfile } from '../types';

export default function UserMenu() {
  const { user, email, groups, signOut } = useAuth();
  const [profile, setProfile] = useState<UserProfile>();

  useEffect(() => {
    if (!user) {
      setProfile(undefined);
      return;
    }
    let cancelled = false;
    void member
      .profile()
      .then((next) => {
        if (!cancelled) setProfile(next);
      })
      .catch(() => {
        if (!cancelled) setProfile(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // `user` is seeded from the cached snapshot on first render (before the
  // Cognito refresh completes), so this branch chooses the right UI shape
  // immediately and avoids a layout flash when auth lands a tick later.
  if (!user) {
    return (
      <NavbarItem href="/login" aria-label="Sign in">
        Sign in
      </NavbarItem>
    );
  }

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  const accountName = profile?.displayName || fullName;
  const initials =
    (accountName || email)
      ?.split('@')[0]
      ?.split(/[._\-\s]+/)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2) ?? '?';
  const label = accountName || email || 'Signed in';

  return (
    <Dropdown>
      <DropdownButton as={NavbarItem} aria-label="Account menu">
        <Avatar
          src={profile?.avatarUrl}
          initials={initials}
          className="size-8"
          square
          alt={label}
        />
      </DropdownButton>
      <DropdownMenu className="min-w-64" anchor="bottom end">
        <DropdownItem disabled>
          <DropdownLabel>{label}</DropdownLabel>
        </DropdownItem>
        {accountName && email ? (
          <DropdownItem disabled>
            <DropdownLabel>{email}</DropdownLabel>
          </DropdownItem>
        ) : null}
        {groups.length > 0 ? (
          <DropdownItem disabled>
            <DropdownLabel>Roles: {groups.join(', ')}</DropdownLabel>
          </DropdownItem>
        ) : null}
        {hasRole(groups, 'admin', 'team-admin', 'scorekeeper') ? (
          <DropdownItem href="/admin">
            <DropdownLabel>Admin</DropdownLabel>
          </DropdownItem>
        ) : null}
        <DropdownItem href="/me">
          <DropdownLabel>My BaseballSTRM</DropdownLabel>
        </DropdownItem>
        <DropdownItem href="/me/profile">
          <DropdownLabel>Profile</DropdownLabel>
        </DropdownItem>
        <DropdownDivider />
        <DropdownItem onClick={() => void signOut()}>
          <DropdownLabel>Sign out</DropdownLabel>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

// Used for legacy spots that need the bare React Router Link (kept for compat).
export const PlainLink = Link;
